import { Alert } from '@mui/material';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';

import { useGetCustomerKYXVerificationQuery } from '@/api/v1';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useTranslator } from '@/hooks/useTranslator';
import { AiPriseResumeButton } from '@/Screens/Dashboard/Customers/AiPriseResumeButton';
import { BeneficiaryType } from '@/types/customer';
import { VerificationStatus } from '@/types/customerKYXVerification';

export const CustomerDetails: React.FC = (): React.ReactElement => {
  const { customerId = '' } = useParams<{ readonly customerId: string }>();
  const tr = useTranslator();
  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerKYXVerificationQuery(customerId, { skip: !customerId });
  const title = tr('CUSTOMERS');

  if (isLoading) {
    return <FullScreenLoader loading />;
  }

  if (isError || !customer) {
    return (
      <MainScreen title={title}>
        <Alert severity="error">{tr('ERROR_LOADING_CUSTOMER')}</Alert>
      </MainScreen>
    );
  }

  const customerName: string | null =
    customer.type === BeneficiaryType.business
      ? customer.businessName
      : customer.type === BeneficiaryType.individual
        ? `${customer.firstName} ${customer.lastName}`
        : null;

  const isVerificationNotStarted: boolean =
    customer.verificationStatus === VerificationStatus.NOT_STARTED;

  const breadcrumbs: React.ReactElement = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        component={Link}
        to="/customers"
        variant="h6"
        color="text.secondary"
        sx={styles.title}>
        {title}
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {'>'}
      </Typography>
      {customerName && <Typography variant="h6">{customerName}</Typography>}
    </Stack>
  );

  return (
    <MainScreen title={breadcrumbs}>
      <Stack gap={3} height="100%">
        <Paper elevation={3} sx={styles.paper}>
          <Stack gap={4}>
            <Stack gap={2}>
              <Stack direction="row" gap={3} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {tr('NAME')}
                </Typography>
                <Typography variant="body2">{customerName}</Typography>
              </Stack>

              <Stack direction="row" gap={3} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {tr('CUSTOMER_TYPE')}
                </Typography>
                <Typography variant="body2">{tr(customer.type)}</Typography>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
              <Stack gap={1} flex={1}>
                <Typography variant="h7">{tr('VERIFICATION_STATUS')}</Typography>

                {isVerificationNotStarted && (
                  <Typography variant="body2" color="text.secondary">
                    {tr('CUSTOMER_VERIFICATION_INFO')}
                  </Typography>
                )}

                <Stack direction="row" gap={0.5} mt={0.5}>
                  <Typography variant="body1" color="text.secondary">
                    {tr('STATUS')}:
                  </Typography>
                  <Typography variant="body1">{tr(customer.verificationStatus)}</Typography>
                </Stack>

                <Stack direction="row" gap={0.5}>
                  <Typography variant="body1" color="text.secondary">
                    {tr('RESULT')}:
                  </Typography>
                  <Typography variant="body1">{tr(customer.verificationResult)}</Typography>
                </Stack>
              </Stack>
              {isVerificationNotStarted && <AiPriseResumeButton />}
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Outlet />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  title: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  paper: {
    p: 5,
    borderRadius: 2,
  },
};
